import { useAccessToken } from "hooks/useAccessToken";
import useAlert from "hooks/useAlert";
import { useMutation, useQueryClient } from "react-query";
import useUploadState from "recoilStore/useUploadState";
import { BACKEND_DOMAIN } from "utils/constants";
import { getMimeType, showMutationAlert } from "utils/helpers";
import { chatbotDocumentsQueryKey } from "ApiLayer/Chatbot/queryChatbotDocuments";
import { documentsQueryKey } from "./queryListDocuments";

interface UploadDocumentParams {
    file: File;
    buildingModelUid: string;
    description?: string;
}

// Chunk size: 5MB
// NOTE: update services/product-platform-api/app/routes/documents.py if changing chunk size
const CHUNK_SIZE = 5 * 1024 * 1024;
// Threshold for chunked upload: 10MB
const CHUNKED_UPLOAD_THRESHOLD = 10 * 1024 * 1024;

interface ChunkUploadResponse {
    upload_uid: string;
    gcs_path: string;
    document_id: number;
    total_chunks: number;
    chunk_urls: Array<{ chunk_index: number; upload_url: string }>;
    is_chunked: boolean;
}

interface SingleUploadResponse {
    upload_url: string;
    gcs_path: string;
    upload_uid: string;
    document_id: number;
}

const useUploadDocuments = () => {
    const alert = useAlert();
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();
    const { addFile, updateFileProgress, setFileCompleted, setFileFailed } = useUploadState();

    const uploadChunk = async (chunkUrl: string, chunk: Blob): Promise<boolean> => {
        const response = await fetch(chunkUrl, {
            method: "PUT",
            headers: {
                "Content-Type": "application/octet-stream",
            },
            body: chunk,
        });
        return response.ok;
    };

    // We are tracking progress to check in the backend that the upload is completed
    const updateProgress = async (
        buildingModelUid: string,
        uploadUid: string,
        uploadedChunks: number
    ): Promise<void> => {
        await fetch(`${BACKEND_DOMAIN}/buildings/${buildingModelUid}/documents/${uploadUid}/progress`, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ uploaded_chunks: uploadedChunks }),
        });
    };

    const finalizeUpload = async (buildingModelUid: string, uploadUid: string): Promise<void> => {
        const response = await fetch(
            `${BACKEND_DOMAIN}/buildings/${buildingModelUid}/documents/${uploadUid}/finalize`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            }
        );
        if (!response.ok) {
            const body = await response.json().catch(() => ({}));
            const message =
                (body as { error?: string })?.error || "Failed to finalize upload";
            throw new Error(message);
        }
    };

    return useMutation({
        mutationFn: async ({ file, buildingModelUid, description }: UploadDocumentParams) => {
            const mimeType = getMimeType(file.name);
            const fileSize = file.size;
            const useChunkedUpload = fileSize > CHUNKED_UPLOAD_THRESHOLD;
            const totalChunks = useChunkedUpload ? Math.ceil(fileSize / CHUNK_SIZE) : 1;

            // Add file to upload state for banner display
            addFile(file.name, totalChunks);

            // Get signed URL(s) from backend
            const signedUrlResponse = await fetch(
                `${BACKEND_DOMAIN}/buildings/${buildingModelUid}/upload`,
                {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    file_name: file.name,
                    mime_type: mimeType,
                    description: description || "",
                    file_size: fileSize,
                    total_chunks: useChunkedUpload ? totalChunks : undefined,
                }),
            });

            if (!signedUrlResponse.ok) {
                const body = await signedUrlResponse.json().catch(() => ({}));
                const message =
                    (body as { error?: string })?.error ||
                    `Failed to get upload URL: ${signedUrlResponse.statusText}`;
                setFileFailed(file.name, message);
                throw new Error(message);
            }

            const responseData = await signedUrlResponse.json();

            // Refresh attach list so the new document appears with its status (e.g. failed for >1GB) even while upload is in progress
            queryClient.invalidateQueries([chatbotDocumentsQueryKey, buildingModelUid]);

            if (useChunkedUpload && responseData.is_chunked) {
                // Chunked upload
                const { upload_uid: uploadUid, chunk_urls: chunkUrls } =
                    responseData as ChunkUploadResponse;

                let uploadedChunks = 0;

                for (let i = 0; i < totalChunks; i++) {
                    const start = i * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, fileSize);
                    const chunk = file.slice(start, end);

                    const chunkUrl = chunkUrls.find((c) => c.chunk_index === i)?.upload_url;
                    if (!chunkUrl) {
                        setFileFailed(file.name, `Missing URL for chunk ${i}`);
                        throw new Error(`Missing URL for chunk ${i}`);
                    }
                    /* eslint-disable no-await-in-loop */
                    const success = await uploadChunk(chunkUrl, chunk);
                    if (!success) {
                        setFileFailed(file.name, `Failed to upload chunk ${i}`);
                        throw new Error(`Failed to upload chunk ${i}`);
                    }

                    uploadedChunks += 1;
                    updateFileProgress(file.name, uploadedChunks);

                    // Update progress on backend
                    await updateProgress(buildingModelUid, uploadUid, uploadedChunks);
                }

                // Finalize the upload (compose chunks)
                await finalizeUpload(buildingModelUid, uploadUid);
            } else {
                // Single file upload
                const { upload_url: uploadUrl } = responseData as SingleUploadResponse;

                const uploadResponse = await fetch(uploadUrl, {
                    method: "PUT",
                    headers: {
                        "Content-Type": mimeType,
                    },
                    body: file,
                });

                if (!uploadResponse.ok) {
                    setFileFailed(file.name, `Upload to GCS failed: ${uploadResponse.statusText}`);
                    throw new Error(`Upload to GCS failed: ${uploadResponse.statusText}`);
                }

                updateFileProgress(file.name, 1);

                const completeResponse = await fetch(`${BACKEND_DOMAIN}/buildings/${buildingModelUid}/documents/${responseData.upload_uid}/finalize_non_chunked_upload`, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });

                if (!completeResponse.ok) {
                    const body = await completeResponse.json().catch(() => ({}));
                    const message =
                        (body as { error?: string })?.error || "Failed to complete upload";
                    setFileFailed(file.name, message);
                    throw new Error(message);
                }
            }

            setFileCompleted(file.name);
            return { file, buildingModelUid };
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries([...documentsQueryKey, data.buildingModelUid]);
            queryClient.invalidateQueries([chatbotDocumentsQueryKey, data.buildingModelUid]);
        },
        onError: (error) => {
            showMutationAlert(error, alert);
        },
    });
};

export default useUploadDocuments;